import React, { useState, useEffect } from "react";
import { TextField, Button, Typography, Grid, Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import Autocomplete from "@mui/material/Autocomplete";
import { get, post } from "../../../../utils/api";
import CarbonFootprintCard from "./CarbonFootPrint";
import LocationAutocomplete from "../../../../Components/Common/LocationAutocomplete";

const CO2EmissionEstimator = () => {
  const [formData, setFormData] = useState({
    vehicle: "",
    fuel: "",
    vehicleAge: "",
    source: "",
    destination: "",
  });
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [carbonData, setCarbonData] = useState(null);
  const [errors, setErrors] = useState({});

  // Fetch vehicle types from API
  useEffect(() => {
    const fetchVehicleTypes = async () => {
      setLoading(true);
      try {
        const response = await get(`/master/getAllVehicle`);
        setVehicleTypes(response.data.data);
      } catch (err) {
        setError("Failed to load vehicle types");
      } finally {
        setLoading(false);
      }
    };

    fetchVehicleTypes();
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: value ? "" : prevErrors[name],
    }));
  };

  const handleLocationChange = (fieldName, value) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      [fieldName]: value,
    }));

    setErrors((prevErrors) => ({
      ...prevErrors,
      [fieldName]: value ? "" : prevErrors[fieldName],
    }));
  };

  const handleVehicleSelect = (event, newValue) => {
    if (newValue) {
      const purchaseDate = new Date(newValue.purchaseDate);
      const currentDate = new Date();
      let age = currentDate.getFullYear() - purchaseDate.getFullYear();
      if (
        currentDate.getMonth() < purchaseDate.getMonth() ||
        (currentDate.getMonth() === purchaseDate.getMonth() &&
          currentDate.getDate() < purchaseDate.getDate())
      ) {
        age--;
      }
      setFormData({
        ...formData,
        vehicle: newValue._id,
        fuel: newValue.fuelType,
        vehicleAge: age.toString(),
      });

      setErrors((prevErrors) => ({
        ...prevErrors,
        vehicle: "",
        fuel: "",
        vehicleAge: "",
      }));
    } else {
      setFormData({
        ...formData,
        vehicle: "",
        fuel: "",
        vehicleAge: "",
      });
    }
  };

  const resetForm = () => {
    setFormData({
      vehicle: "",
      fuel: "",
      vehicleAge: "",
      source: "",
      destination: "",
    });
    setCarbonData(null); // Clear the carbon data result
  };

  // Validation and Submit Functions
  const validateForm = () => {
    let formErrors = {};
    if (!formData.vehicle) formErrors.vehicle = "Vehicle is required";
    if (!formData.fuel) formErrors.fuel = "Fuel type is required";
    if (!formData.vehicleAge || isNaN(formData.vehicleAge))
      formErrors.vehicleAge = "Valid vehicle age is required";
    if (!formData.source) formErrors.source = "Source is required";
    if (!formData.destination)
      formErrors.destination = "Destination is required";
    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    setError(null);

    try {
      const response = await post(`/scope3/co2Emission/add`, formData);
      setCarbonData(response.data);
    } catch (error) {
      setError("An error occurred while submitting the form");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div style={{ padding: "20px", width: "80%" }}>
        <Typography
          variant="h5"
          fontWeight="bold"
          sx={{ textDecoration: "underline" }}
          gutterBottom
        >
          CO₂ Emission Estimator
        </Typography>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: "bold", mt: 2 }}
              >
                Vehicle
              </Typography>
              <Autocomplete
                value={
                  vehicleTypes.find(
                    (vehicle) => vehicle._id === formData.vehicle
                  ) || null
                } // Controlled value
                options={vehicleTypes}
                getOptionLabel={(option) =>
                  `${option.vehicleType} - ${option.vehicleNumber}`
                }
                onChange={handleVehicleSelect}
                renderInput={(params) => (
                  <CustomTextField
                    {...params}
                    placeholder="Tap to Select"
                    error={!!errors.vehicle}
                    // helperText={errors.vehicle}
                    helperText={
                      <Typography
                        variant="body2"
                        color="error"
                        style={{
                          lineHeight: "1.2",
                          margin: "2px 0",
                          color: "#d34040",
                          fontSize: "10px",
                          fontStyle: "italic",
                          marginLeft: "-10px",
                        }}
                      >
                        {errors.vehicle}
                      </Typography>
                    }
                  />
                )}
                loading={loading}
              />

              <Typography
                variant="subtitle1"
                sx={{ fontWeight: "bold", mt: 2 }}
              >
                Fuel Type
              </Typography>
              <CustomTextField
                variant="outlined"
                value={formData.fuel}
                fullWidth
                name="fuel"
                placeholder="autofilled"
                onChange={handleChange}
                error={!!errors.fuel}
                // helperText={errors.fuel}
                InputProps={{ readOnly: true }}
                helperText={
                  <Typography
                    variant="body2"
                    color="error"
                    style={{
                      lineHeight: "1.2",
                      margin: "2px 0",
                      color: "#d34040",
                      fontSize: "10px",
                      fontStyle: "italic",
                      marginLeft: "-10px",
                    }}
                  >
                    {errors.fuel}
                  </Typography>
                }
              />

              <Typography
                variant="subtitle1"
                sx={{ fontWeight: "bold", mt: 2 }}
              >
                Vehicle Age (Years)
              </Typography>
              <CustomTextField
                variant="outlined"
                value={formData.vehicleAge}
                fullWidth
                name="vehicleAge"
                placeholder="autofilled"
                onChange={handleChange}
                error={!!errors.vehicleAge}
                // helperText={errors.vehicleAge}
                InputProps={{ readOnly: true }}
                helperText={
                  <Typography
                    variant="body2"
                    color="error"
                    style={{
                      lineHeight: "1.2",
                      margin: "2px 0",
                      color: "#d34040",
                      fontSize: "10px",
                      fontStyle: "italic",
                      marginLeft: "-10px",
                    }}
                  >
                    {errors.vehicleAge}
                  </Typography>
                }
              />
            </Grid>

            {/* Additional Form Fields and Submit Button */}
            <Grid item xs={12} sm={6}>
              <Box sx={{ marginTop: "1rem" }}>
                <LocationAutocomplete
                  title={"Source"}
                  queryType="city"
                  value={formData.source}
                  onChange={(newValue) =>
                    handleLocationChange("source", newValue)
                  }
                  error={errors.source}
                  placeholder="Enter Source"
                />
              </Box>

              <Box sx={{ marginTop: "1.3rem" }}>
                <LocationAutocomplete
                  title={"Destination"}
                  queryType="city"
                  value={formData.destination}
                  onChange={(newValue) =>
                    handleLocationChange("destination", newValue)
                  }
                  error={errors.destination}
                  placeholder="Enter Destination"
                />
              </Box>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={4}>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{ bgcolor: "#1a3e72" }}
              >
                Estimate Emission
              </Button>
            </Grid>
          </Grid>

          {/* {carbonData && <CarbonFootprintCard carbonData={carbonData} resetForm={resetForm} />} */}
          {error && (
            <Typography
              variant="body1"
              sx={{ mt: 2, color: "red", fontWeight: "bold" }}
            >
              {error}
            </Typography>
          )}
        </form>
      </div>
      {carbonData && (
        <CarbonFootprintCard carbonData={carbonData} resetForm={resetForm} />
      )}
    </div>
  );
};

export default CO2EmissionEstimator;

const CustomTextField = styled(TextField)(({ theme }) => ({
  "& .MuiInputBase-root": {
    height: "40px",
  },
  "& .MuiSelect-select": {
    height: "56px",
    display: "flex",
    alignItems: "center",
  },
}));
